/**
 * useLiveReportDetailsModals — composes frequency + adapters + scroll state for the details screen.
 *
 * Keeps `LiveReportDetailsScreen` a thin wire: this hook owns eligibility focus checks, metadata,
 * modal visibility wiring, and returns ready props for `<NpsModal>` / `<ReferralModal>`.
 */
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useFocusEffect } from "@react-navigation/native"

import { useNpsFrequency } from "../../../hooks/useNpsFrequency"
import { useNpsSurveyAdapter } from "../../../hooks/useNpsSurveyAdapter"
import { useReferralFrequency } from "../../../hooks/useReferralFrequency"
import { useReferralShareAdapter } from "../../../hooks/useReferralShareAdapter"
import { type ReferralCodePayload } from "../../../types/referralCode"
import { useLiveReportDetailsState } from "./useLiveReportDetailsState"

export type LiveReportDetailsModalsInput = {
    hash: string | null
    userEmail: string | null
    engagementScore?: number
    isAdminOrTutor: boolean
    preloadedReferralCode: ReferralCodePayload | null
}

export function useLiveReportDetailsModals(input: LiveReportDetailsModalsInput) {
    const { hash, userEmail, engagementScore, isAdminOrTutor, preloadedReferralCode } = input

    const metadata = useMemo(
        () => ({
            live_report_hash: hash,
            trigger_type: "scroll",
            engagement_score: engagementScore,
        }),
        [hash, engagementScore]
    )

    const { canShowNPS, checkEligibility: checkNpsEligibility, markAsDismissed } = useNpsFrequency(userEmail)

    const { canShowReferralModal, checkEligibility: checkReferralEligibility, markAsShown } = useReferralFrequency()

    const { modalProps: npsModalProps } = useNpsSurveyAdapter({
        userEmail,
        triggerContext: "live_report",
        metadata,
        markAsDismissed,
    })

    const {
        handleDismiss: handleReferralDismiss,
        onModalVisible: onReferralModalVisible,
        modalProps: referralModalProps,
    } = useReferralShareAdapter({
        userEmail,
        triggerContext: "live_report",
        metadata,
        preloadedReferralCode,
        markAsShown,
    })

    const { actions, onScroll, showNpsModal, showReferralModal } = useLiveReportDetailsState({
        shouldCheckModals: !isAdminOrTutor,
        canShowNPS,
        canShowReferralModal,
    })
    const { close, dismissNps, dismissReferral, resetTriggers } = actions

    useFocusEffect(
        useCallback(() => {
            if (!isAdminOrTutor && userEmail) void checkNpsEligibility()
            if (!isAdminOrTutor) void checkReferralEligibility()
            resetTriggers()
        }, [isAdminOrTutor, userEmail, checkNpsEligibility, checkReferralEligibility, resetTriggers])
    )

    const wasReferralVisible = useRef(false)
    useEffect(() => {
        if (showReferralModal && !wasReferralVisible.current) {
            onReferralModalVisible()
        }
        wasReferralVisible.current = showReferralModal
    }, [showReferralModal, onReferralModalVisible])

    const nps = isAdminOrTutor
        ? null
        : {
              visible: showNpsModal,
              onClose: dismissNps,
              ...npsModalProps,
          }

    const referral = isAdminOrTutor
        ? null
        : {
              visible: showReferralModal,
              onClose: () => {
                  handleReferralDismiss()
                  dismissReferral()
              },
              ...referralModalProps,
          }

    return {
        close,
        onScroll: isAdminOrTutor ? undefined : onScroll,
        nps,
        referral,
    }
}
